import {Checkbox} from '@wandb/weave/components';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {capitalizeFirstLetter} from '../../../../util/string';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {ErrorText} from './CreateTriggerDrawerLoaded.styles';
import {MetricNameInput} from './MetricNameInput';
import {RunFilterChunk} from './RunFilterShared';
import {
  getRunMetricZscoreString,
  isValidChangeDirection,
  isValidMetricName,
  isValidThreshold,
  isValidWindowSize,
  RunMetricZscoreActions,
  useRunMetricZscoreStore,
} from './RunMetricZscoreStore';
import {BoldLabel, SeparatedChunk} from './shared';

// Re-export types and functions from store for backwards compatibility
export type {
  RunMetricZscoreConfig,
  ZscoreMetricFilter,
} from './RunMetricZscoreStore';
export {
  getChangeDirectionFromCheckboxes,
  getCheckboxesFromChangeDirection,
  getEventConfigForRunMetricZscore,
  getNumberValueForField,
  getRunMetricZscoreConfig,
  getRunMetricZscoreString,
  isValidChangeDirection,
  isValidMetricName,
  isValidRunMetricZscoreConfig,
  isValidThreshold,
  isValidWindowSize,
  RunMetricZscoreActions,
  useRunMetricZscoreStore,
} from './RunMetricZscoreStore';

export const RunMetricZscoreSection = () => {
  const metricFilter = useRunMetricZscoreStore(
    useShallow(state => state.metricFilter)
  );
  const runFilter = useRunMetricZscoreStore(
    useShallow(state => state.runFilter)
  );

  const showMetricNameError =
    metricFilter.metricName !== undefined &&
    !isValidMetricName(metricFilter.metricName);
  const showThresholdError =
    metricFilter.threshold !== undefined &&
    !isValidThreshold(metricFilter.threshold);
  const showWindowSizeError =
    metricFilter.windowSize !== undefined &&
    !isValidWindowSize(metricFilter.windowSize);
  const showChangeDirectionError = !isValidChangeDirection(
    metricFilter.changeDirection
  );

  const eventDescription = capitalizeFirstLetter(
    getRunMetricZscoreString(metricFilter, runFilter)
  );

  return (
    <Tailwind>
      <div className="mt-16 font-semibold">Metric setup</div>
      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-wb-border p-16">
        <S.AutomationDescriptionSection>
          <S.AutomationDescriptionIcon>
            <Icon name="recent-clock" />
          </S.AutomationDescriptionIcon>
          <S.AutomationDescriptionBody>
            <S.AutomationDescriptionTitle>
              In the event that
            </S.AutomationDescriptionTitle>
            <S.AutomationDescriptionText>
              {eventDescription}
            </S.AutomationDescriptionText>
          </S.AutomationDescriptionBody>
        </S.AutomationDescriptionSection>
        <SeparatedChunk>
          <BoldLabel>Metric</BoldLabel>
          <div className="mt-4">
            <MetricNameInput
              value={metricFilter.metricName}
              onChange={value => RunMetricZscoreActions.setMetricName(value)}
            />
            {showMetricNameError && (
              <ErrorText>Metric name cannot be empty</ErrorText>
            )}
          </div>
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center gap-4">
              Baseline window
              <Tooltip
                content="Defines how many recent logged values are used to calculate the average and standard deviation for detecting outliers."
                trigger={
                  <Icon
                    className="text-moon-400 dark:text-wb-text-tertiary"
                    name="info"
                  />
                }
              />
            </div>
          </BoldLabel>
          <div className="mt-4 flex items-center gap-8">
            <span>Average across</span>
            <div className="mx-2 inline-block w-48">
              <TextField
                type="number"
                value={metricFilter.windowSize.toString()}
                onChange={value => RunMetricZscoreActions.setWindowSize(value)}
                errorState={showWindowSizeError}
              />
            </div>
            <span>logged values.</span>
          </div>
          {showWindowSizeError && (
            <ErrorText>Integer 2 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center gap-4">
              Z-score threshold
              <Tooltip
                content="Z-score is a calculation of how many standard deviations away the current value is from baseline window average."
                trigger={
                  <Icon
                    className="text-moon-400 dark:text-wb-text-tertiary"
                    name="info"
                  />
                }
              />
            </div>
          </BoldLabel>
          <div className="mt-4">
            <div className="inline-block w-48">
              <TextField
                type="number"
                value={metricFilter.threshold.toString()}
                onChange={value => RunMetricZscoreActions.setThreshold(value)}
                errorState={showThresholdError}
              />
            </div>
          </div>
          {showThresholdError && (
            <ErrorText>Threshold must be a positive number</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center">Trigger if z-score is:</div>
          </BoldLabel>
          <div className="mt-4 flex flex-col gap-8">
            <div className="flex items-center gap-8">
              <Checkbox
                size="small"
                checked={metricFilter.changeDirection.isAboveChecked}
                onCheckedChange={checked =>
                  RunMetricZscoreActions.setIsAboveChecked(!!checked)
                }
              />
              <span>Above {metricFilter.threshold}</span>
            </div>
            <div className="flex items-center gap-8">
              <Checkbox
                size="small"
                checked={metricFilter.changeDirection.isBelowChecked}
                onCheckedChange={checked =>
                  RunMetricZscoreActions.setIsBelowChecked(!!checked)
                }
              />
              <span>Below -{metricFilter.threshold}</span>
            </div>
          </div>
          {showChangeDirectionError && (
            <ErrorText>At least one direction must be selected</ErrorText>
          )}
        </SeparatedChunk>
      </div>
      <SeparatedChunk>
        <RunFilterChunk
          runFilter={runFilter}
          actions={{
            ...RunMetricZscoreActions,
          }}
        />
      </SeparatedChunk>
    </Tailwind>
  );
};
